import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import ApiService from "../../services/api.service";
import PlayerModal from "./PlayerModal";
import { SPORT_STATS, DEFAULT_SPORT } from "../../config/sportStats";
import "../client/ClientLayout.css";
import "./CoachPages.css";

function prettyDate(iso) {
  if (!iso) return "";
  const d = new Date(`${iso}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });
}

// Season-averages tiles: every stat field the sport tracks, with basketball's
// offReb/defReb collapsed into one combined REB tile — mirrors the iOS
// CoachPlayerStatsScreen.displayFields convention.
function averageFields(sport) {
  const all = SPORT_STATS[sport] || SPORT_STATS[DEFAULT_SPORT];
  if (sport !== "basketball") return all;
  const out = [];
  let insertedRebounds = false;
  for (const f of all) {
    if (f.key === "offReb" || f.key === "defReb") {
      if (!insertedRebounds) {
        out.push({ key: "reb", label: "Rebounds", abbr: "REB" });
        insertedRebounds = true;
      }
      continue;
    }
    out.push(f);
  }
  return out;
}

// Compact per-game headline stats — the game log's "at a glance" row. The
// season averages grid above already shows every recorded category; this
// stays a fixed short list per sport, mirroring the iOS screen's
// headlineFields (not driven by SPORT_STATS since there's no "headline" flag
// there — this is a small curated subset, in sync between both clients).
const HEADLINE_FIELDS = {
  basketball: [
    { key: "points", abbr: "PTS" },
    { key: "reb", abbr: "REB" },
    { key: "assists", abbr: "AST" },
    { key: "steals", abbr: "STL" },
    { key: "blocks", abbr: "BLK" },
  ],
  soccer: [
    { key: "goals", abbr: "G" },
    { key: "assists", abbr: "A" },
    { key: "shotsOnTarget", abbr: "SOT" },
    { key: "saves", abbr: "SV" },
  ],
};

function statValue(game, key) {
  if (key === "reb") return Math.round((game.stats?.offReb || 0) + (game.stats?.defReb || 0));
  return Math.round(game.stats?.[key] || 0);
}

function average(averages, key) {
  if (key === "reb") return (averages.offReb || 0) + (averages.defReb || 0);
  return averages[key] || 0;
}

export default function CoachPlayerStatsPage() {
  const { playerId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await ApiService.getCoachPlayerStats(playerId);
      setData(resp);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [playerId]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div>
      <Link to="/coach" className="coach-back-link">
        <ArrowLeft size={14} /> Back to roster
      </Link>

      {loading ? (
        <div className="client-loading-row">Loading…</div>
      ) : error ? (
        <div className="client-error">{error}</div>
      ) : data ? (
        <PlayerStats data={data} onEdit={() => setEditing(true)} />
      ) : null}

      {editing && (
        <PlayerModal
          player={{ playerId, ...data.player }}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }}
          onArchived={() => navigate("/coach")}
        />
      )}
    </div>
  );
}

function PlayerStats({ data, onEdit }) {
  const sport = data.sport || DEFAULT_SPORT;
  const fields = averageFields(sport);
  const headline = HEADLINE_FIELDS[sport] || HEADLINE_FIELDS.basketball;
  const games = data.games || [];
  const averages = data.averages || {};

  return (
    <>
      <div className="coach-page-header">
        <div>
          <h1 className="client-h1">{data.player?.name || "Player"}</h1>
          <p className="client-subtitle">
            {data.player?.number ? `#${data.player.number}` : ""}
            {data.player?.position ? ` · ${data.player.position}` : ""}
          </p>
        </div>
        <button className="client-button" onClick={onEdit}>Edit player</button>
      </div>

      {games.length === 0 ? (
        <div className="client-empty">No games recorded yet.</div>
      ) : (
        <>
          <div className="coach-stat-grid">
            <div className="coach-stat-tile">
              <div className="coach-stat-value">{data.totals?.games ?? games.length}</div>
              <div className="coach-stat-label">Games</div>
            </div>
            {fields.map((f) => (
              <div className="coach-stat-tile" key={f.key}>
                <div className="coach-stat-value">{average(averages, f.key).toFixed(1)}</div>
                <div className="coach-stat-label">{f.abbr}</div>
              </div>
            ))}
          </div>

          <div className="coach-history-list">
            {games.map((g) => (
              <div className="coach-history-card" key={g.matchId}>
                <div className="coach-history-row" style={{ cursor: "default" }}>
                  <div>
                    <div className="coach-history-matchup">vs {g.opponentName}</div>
                    <div className="coach-history-date">{prettyDate(g.date)}</div>
                  </div>
                  <div className="coach-history-score">
                    {g.result} {g.teamScore}-{g.opponentScore}
                  </div>
                </div>
                <div className="coach-history-detail coach-game-stat-row">
                  {headline.map((f) => (
                    <div className="coach-stat-cell" key={f.key}>
                      <div className="coach-stat-cell-value">{statValue(g, f.key)}</div>
                      <div className="coach-stat-cell-label">{f.abbr}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}
